import React, { HTMLProps, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { MIJNPENSIOENOVERZICHT_URL } from '../../../config/constants';
import { PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { PensionDataType } from '../../../types/PensionDataType';
import { toPrice } from '../../../utils/parsers';
import { Button } from '../../general/Button';
import { Dropzone } from '../../general/DropZone';
import { ModalScreenPension } from '../../general/ModalScreen';
import { SummaryEntry } from '../../investment-robot/forecast-screen/ForecastResultsCalculations';
import { OverlayHeader } from '../expenses-overlay/OverlayHeader';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { SavingButtons } from './DialogueDataSaver';
import { BackButton } from './NavigationButtons';

export const PensionSection: React.FC<{ isPartnerSection?: boolean }> = ({ isPartnerSection = false }) => {
    const { t } = useTranslation('pension');
    return (
        <CocktailWrapperScreen
            page={3}
            title={isPartnerSection ? t('pension-cocktail-fill-screen-pension-partner-title') : t('pension-cocktail-fill-screen-pension-title')}
            subTitle={isPartnerSection ? t('pension-cocktail-fill-screen-pension-partner-subsubtitle') : t('pension-cocktail-fill-screen-pension-subsubtitle')}
        >
            <PensionSelectionButtons isPartnerSection={isPartnerSection} />
            <SmallLetters className={'my-8'} />
            <XMLModal isPartnerSection={isPartnerSection} />

            <div className="flex flex-row flex-wrap gap-4 items-start my-8">
                <BackButton />
            </div>
            <SavingButtons/>
        </CocktailWrapperScreen>
    );
};

const PensionSelectionButtons: React.FC<{ isPartnerSection?: boolean }> = ({ isPartnerSection = false }) => {
    const { t } = useTranslation('pension');
    const { actions } = useContext(PensionContext);
    const history = useHistory();
    return (
        <div className="flex flex-col gap-4 my-8">
            <div className="flex flex-row flex-wrap gap-4 items-center">
                <span className="opacity-50 ml-4 w-24">{`2 ${t('general-minutes')}`}</span>
                <Button
                    className="px-8 font-semibold"
                    tint="green" 
                    size="sm"
                    onClick={() => actions.setParameters({ pensionInfo: { modal: { show: true } } })}>
                    {t('pension-cocktail-fill-screen-pension-btn-xml')}
                </Button>
            </div>
            <div className="flex flex-row flex-wrap gap-4 items-center">
                <span className="opacity-50 ml-4 w-24">{`10 ${t('general-minutes')}`}</span>
                <Button
                    className="mr-16 px-8 h-10 font-semibold"
                    tint="neutral-no-hover"
                    size="sm"
                    onClick={() => {
                        window.scrollTo(0, 0);
                        history.push(isPartnerSection ? 'pension-aow-partner' : 'pension-aow');
                    }}
                >
                    {isPartnerSection ? t('pension-cocktail-fill-screen-pension-partner-btn-insert-self') : t('pension-cocktail-fill-screen-pension-btn-insert-self')}
                </Button>
            </div>
        </div>
    );
};

const SmallLetters: React.FC<HTMLProps<HTMLDivElement>> = ({ className }) => {
    const { t } = useTranslation('pension');
    return (
        <div className={className}>
            <p className="font-semibold text-sm">{t('pension-cocktail-fill-screen-pension-small-letters-title')}</p>
            <p className="text-sm">{t('pension-cocktail-fill-screen-pension-small-letters-text')}</p>
        </div>
    );
};

const XMLModal: React.FC<{ isPartnerSection?: boolean }> = ({ isPartnerSection = false }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { pensionInfo, personalInfo },
        actions,
    } = useContext(PensionContext);
    const history = useHistory();

    const [pensionInfoFromXml, setPensionInfoFromXml] = useState({} as any);
    const [xmlFileName, setXmlFileName] = useState("");
    //type xmlParseStatusType = 'Success' | 'Fail' | 'Waiting';
    enum xmlParseStatusStyle {
        Success = "bg-bnd-green border-bnd-green",
        Fail = "bg-bnd-red",
        Waiting = "bg-bnd-black-1",

    }
    const [xmlParseStatus, setXmlParseStatus] = useState<xmlParseStatusStyle>(xmlParseStatusStyle.Waiting);

    // TODO big ugly callback to parse the xml and set the aow and pension in the state. Refactor with the other TODO's when it is more clear
    var parser = require('fast-xml-parser');
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            acceptedFiles.forEach((file) => {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const xmlData: PensionDataType = parser.parse(reader.result);

                        const pensionTotals = xmlData.PensioenGegevensType.Totalen.OuderdomsPensioenTotalen.OuderdomsPensioenTotaal;
                        // We only use the last values ( from age x to deceased )
                        const pensionTotal = pensionTotals[pensionTotals.length - 1];
                        setXmlFileName(file.name);
                        if (isPartnerSection) {
                            setPensionInfoFromXml({
                                employeePensionGrossYearly: (pensionTotal.Pensioen + pensionTotal.IndicatiefPensioen) * 100,
                                aowGrossYearly: (pensionTotal.AOWSamenwonend ?? 0) * 100
                            });
                        }
                        else {
                            setPensionInfoFromXml({
                                employeePensionGrossYearly: (pensionTotal.Pensioen + pensionTotal.IndicatiefPensioen) * 100,
                                aowGrossYearly: {
                                    single: (pensionTotal.AOWAlleenstaand ?? 0) * 100,
                                    together: (pensionTotal.AOWSamenwonend ?? 0) * 100,
                                },
                            });
                        }

                        window.scrollTo(0, 0);
                        setXmlParseStatus(xmlParseStatusStyle.Success);
                        //history.push(isPartnerSection || !personalInfo?.isCalculatedTogether ? 'supplement' : 'pension-partner');
                    }
                    catch (e: unknown) {
                        setXmlFileName(t('my-pension-modal-error'));
                        setXmlParseStatus(xmlParseStatusStyle.Fail);
                    }
                };
                reader.readAsText(file);
            });
        },
        [parser, isPartnerSection, t, xmlParseStatusStyle.Fail, xmlParseStatusStyle.Success],
    );

    return (
        <ModalScreenPension
            isShown={!!pensionInfo?.modal?.show}
        >
            <div className="bg-white md:rounded-xl p-8">
                <OverlayHeader
                    title={isPartnerSection ? t('my-pension-modal-partner-title') : t('my-pension-modal-title')}
                    className="mb-8"
                    onClose={() => {
                        actions.setParameters({ pensionInfo: { modal: { show: false } } });
                    }}
                />

                <p className="font-semibold text-base sm:text-xl">
                    {t('my-pension-modal-subtitle')}{' '}
                    <a
                        className="underline"
                        href={MIJNPENSIOENOVERZICHT_URL}
                        rel="noreferrer"
                        target="_blank"
                    >
                        {t('my-pension-modal-website')}
                    </a>.{' '}
                    {t('my-pension-modal-subtitle-2')}
                </p>

                {/* TODO Placeholder for video */}
                <div className="mt-6 mb-8 bg-gray-300 w-64 h-32" />

                <span>{t('my-pension-modal-content')}</span>
                <Dropzone
                    className={"mt-6 mb-6 " + xmlParseStatus}
                    //text={fileName === "" ? t('my-pension-modal-dropzone') : fileName}
                    text={xmlFileName ? xmlFileName : t('my-pension-modal-dropzone')}
                    dragText={t('my-pension-modal-dropzone-dragging')}
                    btn={xmlFileName ? "XML opnieuw uploaden" : t('my-pension-modal-dropzone-btn')}
                    ondrop={onDrop}
                />
                {Object.keys(pensionInfoFromXml).length !== 0 && (
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                            <div className="mb-4">

                                <SummaryEntry
                                    text={t('my-pension-modal-employeepension-label')}
                                    value={toPrice(pensionInfoFromXml.employeePensionGrossYearly, {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    })}
                                />
                                <SummaryEntry
                                    text={t('my-pension-modal-aow-label')}
                                    className={'mt-2'}
                                    value={toPrice(
                                        (isPartnerSection
                                            ? pensionInfoFromXml.aowGrossYearly
                                            : (personalInfo.isCalculatedTogether
                                                ? pensionInfoFromXml.aowGrossYearly.together
                                                : pensionInfoFromXml.aowGrossYearly.single)
                                        ), {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    })}

                                />
                            </div>
                        </div>

                        <div className="mt-2">
                            <Button
                                className="flex flex-row px-8"
                                tint="green"
                                size="sm"
                                onClick={() => {
                                    actions.setParameters(
                                        isPartnerSection
                                            ? { pensionInfo: { partner: pensionInfoFromXml } }
                                            : {
                                                pensionInfo: { self: pensionInfoFromXml }
                                            });
                                    actions.setParameters({
                                        pensionInfo: { modal: { show: false } }
                                    });
                                    setPensionInfoFromXml({});
                                    setXmlFileName("");
                                    setXmlParseStatus(xmlParseStatusStyle.Waiting);
                                    history.push(isPartnerSection || !personalInfo?.isCalculatedTogether ? 'supplement' : 'pension-partner');
                                }
                                }
                            >
                                {t('my-pension-modal-btn')}
                            </Button>
                        </div>
                    </div>
                )}

            </div>
        </ModalScreenPension>
    );
};
